import { ClickAwayListener } from '@mui/material'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import Button from '@returnmates/ui-core/src/components/Button'
import clsx from 'clsx'
import moment from 'moment'
import momentTimezone from 'moment-timezone'
import { memo, useCallback, useMemo, useState } from 'react'

import CalendarIcon from '../images/icons/calendar'
import SingleDatePicker from '../SingleDatePicker'
import useStyles from './styles'

const DEFAULT_SCHEDULER_LIMIT = 7

interface Props {
  onDateUpdate: (date: string) => void
  isLoading: boolean
  isTaskTypeFilterEnabled?: boolean
  show?: boolean
  schedulerLimit?: number | null
  notes?: string
}

function Calendar({
  onDateUpdate,
  isLoading,
  isTaskTypeFilterEnabled,
  show = true,
  schedulerLimit = DEFAULT_SCHEDULER_LIMIT,
  notes,
}: Props) {
  const classes = useStyles({ isTaskTypeFilterEnabled })
  const [showDatePicker, setShowDatePicker] = useState(false)
  const [date, setDate] = useState(momentTimezone(new Date()).format('YYYY-MM-DD'))

  const toggleDatePicker = useCallback(() => {
    setShowDatePicker(!showDatePicker)
  }, [showDatePicker])

  const onCancel = useCallback(() => {
    setShowDatePicker(false)
  }, [])

  const onDateChange = useCallback(
    date => {
      setDate(moment(date).format('YYYY-MM-DD'))
    },
    [setDate],
  )

  const onDateApply = useCallback(() => {
    setShowDatePicker(false)
    onDateUpdate(date)
  }, [onDateUpdate, date])

  const tooltipTitle = useMemo(
    () =>
      isTaskTypeFilterEnabled
        ? 'Change Package Date'
        : 'To reschedule, select EITHER pickup or delivery tasks/packages',
    [isTaskTypeFilterEnabled],
  )

  const maxDate = useMemo(() => {
    if (!schedulerLimit) return null
    return moment()
      .add(schedulerLimit, 'days')
      .format('YYYY-MM-DD')
  }, [schedulerLimit])

  return (
    <div className={clsx(classes.calendarWrapper, !show && classes.hidden)}>
      <Tooltip
        classes={{
          tooltip: classes.tooltip,
        }}
        title={tooltipTitle}
      >
        <span>
          <IconButton
            className={classes.iconButton}
            onClick={toggleDatePicker}
            disabled={isLoading}
          >
            <CalendarIcon className={classes.calendarIcon} />
          </IconButton>
        </span>
      </Tooltip>
      {showDatePicker ? (
        <ClickAwayListener onClickAway={toggleDatePicker}>
          <div className={classes.toolbarDatePicker}>
            <SingleDatePicker
              value={date}
              onChange={onDateChange}
              disablePast
              isStatic
              momentFormat="YYYY-MM-DD"
              maxDate={maxDate}
            />
            {!!notes && (
              <div className={classes.notes}>{`Reschedule window shortened for ${notes}`}</div>
            )}
            <div className={classes.modalButtons}>
              <Button
                className={clsx(classes.button, classes.cancelButton)}
                onClick={onCancel}
                label="Cancel"
              />
              <Button
                className={clsx(classes.button, classes.applyButton)}
                onClick={onDateApply}
                label="Save"
              />
            </div>
          </div>
        </ClickAwayListener>
      ) : null}
    </div>
  )
}

export default memo(Calendar)
